import React, { useState, useEffect, useCallback } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import HeadingPermissionDialog from "./dialog/HeadingPermissionDialog";
import { isIOS } from "../utils/gps/deviceOrientation";
import { USE_GPS } from "../../config/gpsConfig";
import { USE_MOCK_GPS } from "../../config/gpsDebugConfig";
import { isInNativeApp, hasNativeGpsPermission } from "../utils/nativeBridge";

const MapPermissionGuard = ({ children }) => {
  const [status, setStatus] = useState("LOADING");
  // gpsEnabled: "granted" | "denied" | "retry"
  // - "granted": GPS許可済み
  // - "denied": ユーザーが明示的に拒否
  // - "retry": 一時的なエラー(タイムアウト/電波不良)で再チャレンジ可能
  const [gpsEnabled, setGpsEnabled] = useState("denied");

  // GPS状態を更新する関数
  const updateGpsEnabled = useCallback((newState) => {
    setGpsEnabled(newState);
  }, []);

  // iosで使用するための方位磁針権限ダイアログ管理（androidでは不要）
  const [headingDialogOpen, setHeadingDialogOpen] = useState(false);

  const finalize = useCallback((gpsState) => {
    setGpsEnabled(gpsState);
    setStatus("READY");
  }, []);

  const handleDialogClose = () => {
    setHeadingDialogOpen(false);
    finalize("granted");
  };

  useEffect(() => {
    const checkIOSHeadingPermission = () => {
      if (
        isIOS() &&
        typeof window.DeviceOrientationEvent?.requestPermission === "function"
      ) {
        window.DeviceOrientationEvent.requestPermission()
          .then((permissionState) => {
            if (permissionState === "granted") {
              // 許可済み
              finalize("granted");
            } else {
              // 拒否状態
              setHeadingDialogOpen(true);
            }
          })
          .catch((e) => {
            setHeadingDialogOpen(true);
          });
      } else {
        finalize("granted");
      }
    };

    // ネイティブアプリ内の場合
    if (isInNativeApp()) {
      if (USE_GPS) {
        const nativePermission = hasNativeGpsPermission();
        finalize(nativePermission ? "granted" : "retry");
      } else {
        finalize("denied");
      }
      return;
    }

    // ブラウザで直接開いた場合
    if (!USE_GPS) {
      finalize("denied");
      return;
    }

    if (USE_MOCK_GPS) {
      finalize("granted");
      return;
    }

    if (!navigator.geolocation) {
      console.warn("Geolocation not supported");
      finalize("denied");
      return;
    }

    // タイムアウト設定 (3秒以内に応答がなければretry状態で開始)
    const timeoutId = setTimeout(() => {
      finalize("retry");
    }, 3000);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timeoutId);
        // iOSの方位磁針権限チェックが必要か確認
        checkIOSHeadingPermission();
      },
      (err) => {
        if (timeoutId) clearTimeout(timeoutId);

        console.warn("GPS init failed", err);

        // エラーコードによる分岐
        if (err.code === 1) {
          // Code 1: PERMISSION_DENIED (ユーザーが拒否)
          finalize("denied");
        } else {
          // Code 3: TIMEOUT (タイムアウト)
          // Code 2: POSITION_UNAVAILABLE (電波状況などで一時的に取れない)
          // -> 「今は取れないだけ」なので "retry" を渡し、
          //    componentDidMountのwatchPositionで再チャレンジさせる
          finalize("retry");
        }
      },
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 3000 }
    );
  }, [finalize]);

  const handleHeadingApprove = async () => {
    setHeadingDialogOpen(false);
    try {
      await window.DeviceOrientationEvent.requestPermission();
    } catch (e) {
      console.error(e);
    } finally {
      finalize("granted");
    }
  };

  if (status === "LOADING" && !headingDialogOpen) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#F4F4F4",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <HeadingPermissionDialog
        open={headingDialogOpen}
        onClose={handleDialogClose}
        onApprove={handleHeadingApprove}
      />

      {status === "READY" && children({ gpsEnabled, updateGpsEnabled })}
    </>
  );
};

export default MapPermissionGuard;
